"use client";

import { useState } from "react";
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Play, Copy, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import Image from "next/image";

const models = [
  "claude-3-5-haiku-20241022",
  "claude-sonnet-4-5-20250514",
  "claude-3-5-sonnet-20241022",
];

export default function PlaygroundPage() {
  const t = useTranslations("playground");
  const tF = useTranslations("playground.fields");

  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(models[0]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userMessage, setUserMessage] = useState(tF("userMessageDefault"));
  const [maxTokens, setMaxTokens] = useState("1024");
  const [temperature, setTemperature] = useState("0.7");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [latency, setLatency] = useState<number | null>(null);
  const [testStatus, setTestStatus] = useState<"idle" | "success" | "error">("idle");

  const baseUrl = typeof window !== "undefined"
    ? (process.env.NEXT_PUBLIC_GATEWAY_URL || "/v1")
    : "/v1";

  const handleRun = async () => {
    if (!apiKey || !userMessage) return;
    setLoading(true);
    setResponse("");
    setLatency(null);
    setTestStatus("idle");

    const messages = [];
    if (systemPrompt) messages.push({ role: "system", content: systemPrompt });
    messages.push({ role: "user", content: userMessage });

    const body = {
      model,
      messages,
      max_tokens: Number(maxTokens),
      temperature: Number(temperature),
    };

    const start = Date.now();
    try {
      const res = await fetch(`${baseUrl}/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(body),
      });
      setLatency(Date.now() - start);

      if (res.ok) {
        const data = await res.json();
        setResponse(JSON.stringify(data, null, 2));
        setTestStatus("success");
        toast.success("✅ API Key working! Response received.");
      } else {
        const err = await res.text();
        setResponse(`Error ${res.status}:\n${err}`);
        setTestStatus("error");
        toast.error(`❌ Error ${res.status}: Check your API key or parameters`);
      }
    } catch (err) {
      setLatency(Date.now() - start);
      const errMsg = err instanceof Error ? err.message : "Network error";
      setResponse(`Error: ${errMsg}`);
      setTestStatus("error");
      toast.error(`❌ Network Error: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const curlSnippet = `curl -X POST ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer ${apiKey || "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "max_tokens": ${maxTokens},
    "temperature": ${temperature},
    "messages": [
      ${systemPrompt ? `{"role": "system", "content": "${systemPrompt}"},\n      ` : ""}{"role": "user", "content": "${userMessage}"}
    ]
  }'`;

  const pythonSnippet = `import requests

response = requests.post(
    "${baseUrl}/chat/completions",
    headers={
        "Authorization": "Bearer ${apiKey || "YOUR_API_KEY"}",
        "Content-Type": "application/json",
    },
    json={
        "model": "${model}",
        "max_tokens": ${maxTokens},
        "temperature": ${temperature},
        "messages": [
            ${systemPrompt ? `{"role": "system", "content": "${systemPrompt}"},\n            ` : ""}{"role": "user", "content": "${userMessage}"}
        ],
    },
)

if response.status_code == 200:
    print("✅ Success!")
    print(response.json())
else:
    print(f"❌ Error {response.status_code}")
    print(response.text)`;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("📋 Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b px-4 h-14 flex items-center gap-4 bg-white">
        <Link href="/dashboard">
          <Button variant="ghost" size="icon"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <Link href="/dashboard" className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
          <Image
            src="/DP-Logo-Ethan.jpg"
            alt="Ethan AI"
            width={32}
            height={32}
            className="h-8 w-8 object-contain"
          />
        </Link>
        <div>
          <span className="font-bold text-lg">Ethan AI Playground</span>
          <p className="text-xs text-gray-500">Test your API key • Learn OpenAI API format</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Hero Section */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h2 className="font-semibold text-blue-900 mb-2">🎮 How to use Playground</h2>
          <ol className="text-sm text-blue-800 space-y-1">
            <li><strong>Step 1:</strong> Paste your API Key (from Dashboard → API Keys)</li>
            <li><strong>Step 2:</strong> Choose a model & configure parameters (optional)</li>
            <li><strong>Step 3:</strong> Enter your prompt/question below</li>
            <li><strong>Step 4:</strong> Click "Run Test" → See response on the right</li>
            <li><strong>Step 5:</strong> Copy the code snippet (cURL or Python) to your app</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT: Request Configuration */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">📋 Request Configuration</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Fill in your API key & message to test</p>
              </CardHeader>
              <CardContent className="space-y-4">

                {/* Step 1: API Key */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="font-semibold text-sm flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">1</span>
                    API Key (Required)
                  </Label>
                  <p className="text-xs text-gray-600">Get from Dashboard → API Keys → Copy your key</p>
                  <Input
                    type="text"
                    placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="font-mono text-xs"
                  />
                  {apiKey && <p className="text-xs text-green-600">✅ API Key detected</p>}
                </div>

                {/* Step 2: Model Selection */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="font-semibold text-sm flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">2</span>
                    Model Selection
                  </Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 3: Parameters */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="font-semibold text-sm flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">3</span>
                    Parameters (Optional)
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Max Tokens (output length)</p>
                      <Input
                        type="number"
                        value={maxTokens}
                        onChange={(e) => setMaxTokens(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Temperature (0-2, higher=creative)</p>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={temperature}
                        onChange={(e) => setTemperature(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Step 4: System Prompt */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="font-semibold text-sm flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">4</span>
                    System Prompt (Optional)
                  </Label>
                  <p className="text-xs text-gray-600">Give AI behavior instructions (e.g., "Be concise", "Speak like Jarvis")</p>
                  <Textarea
                    placeholder="e.g., 'Kamu adalah asisten yang helpful dan concise'"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                </div>

                {/* Step 5: User Message */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="font-semibold text-sm flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">5</span>
                    Your Question / Prompt (Required)
                  </Label>
                  <p className="text-xs text-gray-600">Enter the message you want to send to AI</p>
                  <Textarea
                    value={userMessage}
                    onChange={(e) => setUserMessage(e.target.value)}
                    rows={4}
                    className="text-sm"
                  />
                </div>

                {/* Run Button */}
                <Button
                  onClick={handleRun}
                  disabled={loading || !apiKey || !userMessage}
                  className="w-full gap-2 h-10 text-base font-semibold bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" /> Running Test...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" /> Run Test
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 text-center">Sends a request to Ethan API & shows response below →</p>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Response + Code Examples */}
          <div className="space-y-4">
            {/* Response Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">📤 API Response</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">Result from Ethan API server</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {testStatus === "success" && (
                      <div className="flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle className="w-4 h-4" /> Success
                      </div>
                    )}
                    {testStatus === "error" && (
                      <div className="flex items-center gap-1 text-red-600 text-xs">
                        <AlertCircle className="w-4 h-4" /> Error
                      </div>
                    )}
                    {latency && <span className="text-xs text-gray-500">{latency}ms</span>}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!response && !loading && (
                  <pre className="bg-gray-100 text-gray-600 rounded-lg p-4 text-xs min-h-[300px] flex items-center justify-center text-center">
                    👇 Click "Run Test" above{"\n"}to see response here
                  </pre>
                )}
                {loading && (
                  <pre className="bg-gray-100 text-gray-600 rounded-lg p-4 text-xs min-h-[300px] flex items-center justify-center">
                    <div className="text-center">
                      <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Testing API...{"\n"}This may take a few seconds
                    </div>
                  </pre>
                )}
                {response && (
                  <pre className={`rounded-lg p-4 text-xs overflow-auto max-h-[400px] min-h-[300px] font-mono ${
                    testStatus === "success"
                      ? "bg-green-900 text-green-100"
                      : "bg-red-900 text-red-100"
                  }`}>
                    {response}
                  </pre>
                )}
              </CardContent>
            </Card>

            {/* Code Examples Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">💻 Use in Your App</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Copy & paste these code snippets to your project</p>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="curl" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="curl">cURL (Command Line)</TabsTrigger>
                    <TabsTrigger value="python">Python</TabsTrigger>
                  </TabsList>

                  <TabsContent value="curl" className="mt-4 space-y-2">
                    <p className="text-xs text-gray-600">Run this in your terminal:</p>
                    <div className="relative group">
                      <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs overflow-auto max-h-[250px] font-mono">
                        {curlSnippet}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleCopy(curlSnippet)}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="python" className="mt-4 space-y-2">
                    <p className="text-xs text-gray-600">Add to your Python project:</p>
                    <div className="relative group">
                      <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs overflow-auto max-h-[250px] font-mono">
                        {pythonSnippet}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleCopy(pythonSnippet)}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
